import { partition } from 'lodash';
import concat from 'lodash/concat';
import delay from 'lodash/delay';
import differenceBy from 'lodash/differenceBy';
import keyBy from 'lodash/keyBy';
import orderBy from 'lodash/orderBy';
import pick from 'lodash/pick';
import Papa from 'papaparse';
import { useEffect, useState } from 'react';

import { axios } from '@/services/axios';
import { downloadCSV } from '@/utils/utils';

import {
    CONFIG,
    CSV_TYPE,
    CSV_TYPE_ERROR,
    CSV_TYPE_FX,
    DEFAULT_VERIFICATION_RESULT,
    IGNORED_MESSAGE,
    INVALID_PATTERN,
    INVALID_RATE_ERROR,
    INVALID_SIZE,
    NO_DATA_FOUND_ERROR,
    RATE_DELETED,
    SUCCESS_MESSAGE,
    VERIFICATION_CHECKS,
    INVALID_DATA_FILENAME,
    API_RESULT_FIELDS_TO_EXPORT,
    DEFAULT_JSON_DATA,
} from '../csv/constants';
import {
    dataIsUnchangedFilter,
    haveErrorsFilter,
    isNegativeRate,
} from '../csv/helper';
import useCSVTemplate from '../csv/useCSVTemplate';

export default function useCSVNumberBlockRates() {
    const [verificationChecks, setVerificationChecks] = useState([]);
    const [uploadData, setUploadData] = useState(null);
    const [dataJSON, setDataJSON] = useState(null);
    const [ratesState, setRatesState] = useState({
        currentRatesLookup: {},
        ratesData: [],
    });
    const lookup = {
        currentRatesLookup: ratesState.currentRatesLookup,
    };

    const [payloads, setPayloads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [verificationResult, setVerificationResult] = useState(
        DEFAULT_VERIFICATION_RESULT,
    );
    const { planID } = useCSVTemplate();
    const RATES = CONFIG.numberBlockRates.rateFields;
    const requiredFieldKeys = CONFIG.numberBlockRates.requiredFields.slice(1);

    const changeVerificationChecks = (i) => {
        delay(
            (index) => {
                setVerificationChecks((prev) => {
                    const newVerificationChecks = [...prev];
                    newVerificationChecks[index].check = true;
                    if (index < newVerificationChecks.length - 1) {
                        newVerificationChecks[index + 1].current = true;
                    }
                    return newVerificationChecks;
                });
            },
            750,
            i,
        );
    };

    const changeVerificationProgresses = (i) => {
        delay(
            (index) => {
                setVerificationChecks((prev) => {
                    const newVerificationChecks = [...prev];
                    newVerificationChecks[index].completionCounter += 1;
                    return newVerificationChecks;
                });
            },
            750,
            i,
        );
    };

    const fetchRateData = async () => {
        try {
            const ratesResponse = await axios.get(
                `/numberblockrates/${planID}`,
            );
            changeVerificationProgresses(1);
            const newLookup = {
                currentRatesLookup: keyBy(ratesResponse.data, 'id'),
            };
            changeVerificationProgresses(1);
            changeVerificationChecks(1);
            setRatesState({
                currentRatesLookup: newLookup.currentRatesLookup,
                ratesData: ratesResponse.data,
            });
        } catch (e) {
            setError(e);
            console.error(e);
            throw new Error(e);
        }
    };

    //file change handler to Upload CSV
    const handleUploadData = (e) => {
        setVerificationChecks(VERIFICATION_CHECKS);
        if (
            e.target.files[0].type === CSV_TYPE ||
            e.target.files[0].type === CSV_TYPE_FX
        ) {
            const data = e.target.files[0];
            //save data
            setLoading(true);
            setUploadData(data);
            changeVerificationProgresses(0);
            changeVerificationChecks(0);

            //fetch data
            fetchRateData();
        } else {
            window.alert(CSV_TYPE_ERROR);
        }
    };

    //reset the whole UI states
    const handleReset = () => {
        setUploadData(null);
        setDataJSON(null);
        setVerificationResult(DEFAULT_VERIFICATION_RESULT);
        setPayloads([]);
        setLoading(false);
        setError(null);
        setRatesState({
            currentRatesLookup: {},
            ratesData: [],
        });
        setVerificationChecks((prev) => {
            const newVerificationChecks = [...prev];
            newVerificationChecks.forEach((v, index) => {
                v.check = false;
                v.current = index === 0; // NOTE: restart at Received CSV check
                v.completionCounter = 0;
            });
            return newVerificationChecks;
        });
    };

    const convertCSVDataToJSON = async (data) => {
        return new Promise((resolve) => {
            const papaparseConfig = {
                dynamicTyping: true, //for numbers type
                header: true, //CSV has headers
                worker: true, //use worker thread
                complete: function (results) {
                    return resolve(results);
                },
            };
            Papa.parse(data, papaparseConfig);
        });
    };

    const reportResult = ({
        noNeedValidationWithErrors,
        needValidationWithErrors,
        unchanged,
        deletedRates,
    }) => {
        const failed = concat(
            noNeedValidationWithErrors.filter(haveErrorsFilter),
            needValidationWithErrors.filter(haveErrorsFilter),
        );
        const success = differenceBy(
            concat(
                noNeedValidationWithErrors,
                needValidationWithErrors,
                deletedRates,
            ),
            failed,
            'id',
        );
        setVerificationResult((prev) => {
            const newVerificationResult = { ...prev };
            newVerificationResult.passed.data = orderBy(
                concat(unchanged, success).map((v) => ({
                    ...v,
                    setupFee: v.setupFee ?? 0,
                    rate: v.rate ?? 0,
                    verificationStatus: v.ignored
                        ? IGNORED_MESSAGE
                        : v.isActive
                          ? SUCCESS_MESSAGE
                          : RATE_DELETED,
                })),
                ['verificationStatus', 'size'],
                ['desc', 'asc'],
            );
            newVerificationResult.failed.data = orderBy(
                failed,
                ['size'],
                ['asc'],
            );
            return newVerificationResult;
        });

        return { allSucceedData: success };
    };

    const transformJSONToCSV = (data) => {
        return data.map((rate) => {
            return Object.keys(API_RESULT_FIELDS_TO_EXPORT).reduce(
                (acc, key) => {
                    const value = rate[key];
                    const newKey = API_RESULT_FIELDS_TO_EXPORT[key];
                    acc[newKey] = value;
                    if (key === 'isActive' || key === 'bill') {
                        acc[newKey] = value === true ? 1 : 0;
                    }
                    return acc;
                },
                {},
            );
        });
    };

    // convert CSV columns into friendlier BE's JSON fields
    const convertCSVHeaders = (csv) => {
        return csv.map((row) => {
            let newRow = { ...DEFAULT_JSON_DATA };
            Object.entries(CONFIG.numberBlockRates.fields).forEach(
                ([beField, csvColumn]) => {
                    newRow[beField] = row[csvColumn];
                },
            );
            newRow.isNewRate = !lookup.currentRatesLookup[newRow.id];
            newRow.isActive = newRow?.isNewRate
                ? true
                : Boolean(newRow.isActive);
            newRow.bill = newRow?.isNewRate ? true : Boolean(newRow.bill); // default bill is true
            newRow.pattern = newRow?.pattern ? String(newRow.pattern) : null;
            newRow.prefix = newRow?.prefix ? String(newRow.prefix) : null;
            return newRow;
        });
    };

    const filterChangedData = (csv) => {
        const [rowsWithId, rowsNoId] = partition(csv, (row) => Boolean(row.id));
        const deletedRates = ratesState?.ratesData.filter(
            (beRow) => !rowsWithId.find((csvRow) => csvRow.id === beRow.id),
        );
        const fieldsToCompare = Object.keys(CONFIG.numberBlockRates.fields);
        const [unchanged, changed] = partition(rowsWithId, (row) => {
            const originalRow = lookup.currentRatesLookup[row.id];
            const csvRow = csv.find((v) => v.id === row.id);
            const pickedRow = pick(csvRow, fieldsToCompare);
            const pickedOriginalRow = pick(originalRow, fieldsToCompare);
            return dataIsUnchangedFilter(pickedOriginalRow, pickedRow);
        });
        const newRates = rowsNoId.filter((row) => {
            return ['size', 'prefix', 'pattern', 'setupFee', 'rate'].some(
                (field) => row[field],
            );
        });

        return {
            changed,
            unchanged: unchanged.map((v) => ({
                ...v,
                ignored: true,
            })),
            newRates: newRates.map((v, index) => ({
                ...v,
                id: index, // tempId to lookup with errors
            })),
            deletedRates: deletedRates.map((v) => ({
                ...v,
                isActive: false,
            })),
        };
    };

    const verifyRates = (data) => {
        const [existingRates, newRates] = partition(data, (row) => {
            const originRate = lookup.currentRatesLookup[row.id];
            return originRate;
        });
        const validateRate = (row) => {
            const hasRequiredFields = requiredFieldKeys.every((field) => {
                if (field === 'prefix' || field === 'pattern') {
                    return (
                        (row.prefix && !row.pattern) ||
                        (!row.prefix && row.pattern)
                    );
                }
                return [1, 5, 10, 100].includes(row[field]);
            });
            const hasValidNumbers = !RATES.some((rate) =>
                isNegativeRate(row[rate]),
            );
            return hasRequiredFields && hasValidNumbers;
        };
        const categoriseRates = (rates) =>
            partition(rates, (row) => validateRate(row));

        const [validExistingRates, invalidExistingRates] =
            categoriseRates(existingRates);
        const [validNewRates, invalidNewRates] = categoriseRates(newRates);
        return {
            validExistingRates,
            validNewRates,
            invalidExistingRates,
            invalidNewRates,
        };
    };

    /**
     * convert invalid JSON data back to CSV with papaparse.
     * */
    const handleExportInvalidData = () => {
        const invalidData = transformJSONToCSV(
            dataJSON.filter(haveErrorsFilter),
        );
        const res = Papa.unparse(invalidData);
        downloadCSV(res, INVALID_DATA_FILENAME);
    };

    const flagDataWithErrors = ({
        noValidationRatesInvalid = [],
        validationRatesInvalid = [],
        data = [],
    }) => {
        INVALID_PATTERN;
        INVALID_SIZE;
        INVALID_RATE_ERROR;

        // Helper to validate fields and collect errors
        const collectFieldErrors = (entry, fieldsWithErrors) => {
            const errors = new Set();
            if (
                !entry?.prefix &&
                !entry?.pattern &&
                fieldsWithErrors.some((field) =>
                    ['prefix', 'pattern'].includes(field),
                )
            )
                errors.add(INVALID_PATTERN);
            if (
                ![1, 5, 10, 100].includes(entry?.size) &&
                fieldsWithErrors.includes('size')
            )
                errors.add(INVALID_SIZE);
            if (RATES.some((rate) => isNegativeRate(entry[rate])))
                errors.add(INVALID_RATE_ERROR);
            return Array.from(errors);
        };

        // Collect all errors for the provided invalid rates
        const mapEntriesWithErrors = (
            entries,
            additionalError,
            fieldsWithErrors = [],
        ) => {
            return entries.map((entry) => ({
                ...entry,
                errors: [
                    ...new Set([
                        ...collectFieldErrors(entry, fieldsWithErrors),
                        ...(additionalError ? [additionalError] : []),
                    ]),
                ],
            }));
        };

        // Combine errors from all categories
        const allErrors = [
            ...mapEntriesWithErrors(
                noValidationRatesInvalid,
                null,
                concat(requiredFieldKeys, RATES),
            ),
            ...mapEntriesWithErrors(
                validationRatesInvalid,
                null,
                concat(requiredFieldKeys, RATES),
            ),
        ];

        // Build a lookup for errors by id
        const dataJSONTmp = {};
        allErrors.forEach(({ id, errors }) => {
            if (!dataJSONTmp[id]) {
                dataJSONTmp[id] = [];
            }
            dataJSONTmp[id].push(...errors);
        });

        // Append errors to the original data, give unique errors
        const newDataWithErrors = data.map((entry) => ({
            ...entry,
            errors: [...new Set(dataJSONTmp[entry.id])] || [],
        }));
        return { newDataWithErrors };
    };
    const processCSVData = async () => {
        const { data } = await convertCSVDataToJSON(uploadData);
        changeVerificationProgresses(1);

        const convertedData = convertCSVHeaders(data);
        changeVerificationProgresses(2);

        setDataJSON(convertedData);

        const { unchanged, changed, newRates, deletedRates } =
            filterChangedData(convertedData);
        changeVerificationProgresses(2);
        changeVerificationChecks(2);

        if (
            changed.length < 1 &&
            newRates.length < 1 &&
            deletedRates.length < 1
        ) {
            setError(NO_DATA_FOUND_ERROR);
        }

        const { validExistingRates, invalidExistingRates } =
            verifyRates(changed);

        const { validNewRates, invalidNewRates } = verifyRates(newRates);

        changeVerificationProgresses(3);
        changeVerificationChecks(3);

        const { newDataWithErrors: noNeedValidationWithErrors } =
            flagDataWithErrors({
                noValidationRatesInvalid: invalidExistingRates,
                data: changed,
            });

        const { newDataWithErrors: needValidationWithErrors } =
            flagDataWithErrors({
                validationRatesInvalid: invalidNewRates,
                data: newRates,
            });

        changeVerificationProgresses(4);
        changeVerificationChecks(4);
        const { allSucceedData } = reportResult({
            noNeedValidationWithErrors,
            needValidationWithErrors,
            deletedRates,
            unchanged,
        });

        transformDataToPayload({
            data: allSucceedData,
            noValidationRatesOKAndExist: validExistingRates,
            validNewRates,
        });
    };

    const transformDataToPayload = ({
        data,
        noValidationRatesOKAndExist,
        validNewRates,
    }) => {
        const newPayload = data.map((row) => {
            const apiData = lookup.currentRatesLookup[row.id];
            const ratesData = {
                ...apiData,
                ...row,
            };
            const doesNotNeedLookup = noValidationRatesOKAndExist?.find(
                (rate) => rate.id === row.id,
            );
            const removeTempId = validNewRates?.find(
                (rate) => rate.id === row.id,
            );
            if (doesNotNeedLookup) return ratesData;
            return {
                ...ratesData,
                id: removeTempId ? null : row.id,
            };
        });
        setPayloads(newPayload);
    };

    /**
     * once lookups are avaialbe, process our uploaded CSV.
     * */
    useEffect(() => {
        processCSVData();
    }, [ratesState?.currentRatesLookup]);

    return {
        error,
        loading,
        payloads,
        uploadData,
        handleUploadData,
        handleReset,
        handleExportInvalidData,
        verificationChecks,
        verificationResult,
    };
}
